import React, { useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ArrowBack, MenuBook } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ContentArea, ContentTitle } from './Guide.styles';
import { BRAND_COLORS } from '../../styles/theme';
import { useTranslation } from '../../i18n';
import { ALL_SECTION_IDS } from './guideSections';
import { UploadFlightDataSection } from './sections/UploadFlightDataSection';
import { ShareFlightsSection } from './sections/ShareFlightsSection';
import { MaintenanceRecordsSection } from './sections/MaintenanceRecordsSection';
import { HealthPanelSection } from './sections/HealthPanelSection';
import { DroneNotesSection } from './sections/DroneNotesSection';
import { LlmChatSection } from './sections/LlmChatSection';
import { InviteUsersSection } from './sections/InviteUsersSection';

interface GuideContentProps {
  onActiveSectionChange: (id: string) => void;
}

export const GuideContent: React.FC<GuideContentProps> = ({ onActiveSectionChange }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost visible entry
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          onActiveSectionChange(visible[0].target.id);
        }
      },
      {
        rootMargin: '-24px 0px -60% 0px',
        threshold: 0,
      }
    );

    ALL_SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [onActiveSectionChange]);

  return (
    <ContentArea>
      <ContentTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MenuBook sx={{ fontSize: 32, color: BRAND_COLORS.forestGreen.main }} />
          <div>
            <Typography variant="h4" sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 600 }}>
              {t('guide.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('guide.subtitle')}
            </Typography>
          </div>
        </Box>
        <Button
          onClick={() => navigate('/')}
          startIcon={<ArrowBack />}
          variant="outlined"
          size="small"
          sx={{
            borderColor: BRAND_COLORS.yellow.main,
            color: BRAND_COLORS.yellow.main,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            '&:hover': {
              borderColor: BRAND_COLORS.yellow.main,
              backgroundColor: `${BRAND_COLORS.yellow.main}10`,
            },
          }}
        >
          {t('guide.backToDashboard')}
        </Button>
      </ContentTitle>
      <UploadFlightDataSection />
      <ShareFlightsSection />
      <MaintenanceRecordsSection />
      <HealthPanelSection />
      <DroneNotesSection />
      <LlmChatSection />
      <InviteUsersSection />
    </ContentArea>
  );
};
